/**
 * ViewDetailsPage - Comprehensive view details page
 *
 * Displays full view information with tabbed interface for:
 * - Overview stats and configuration
 * - Data preview with query controls
 * - Rules configuration
 * - Column mappings
 * - Matched files
 * - Refresh history
 * - Generated SQL
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useView } from '@/hooks/useViews';
import { ViewHeader } from '@/components/views/details/ViewHeader';
import { OverviewTab } from '@/components/views/details/OverviewTab';
import { DataPreviewTab } from '@/components/views/details/DataPreviewTab';
import { RulesTab } from '@/components/views/details/RulesTab';
import { ColumnsTab } from '@/components/views/details/ColumnsTab';
import { FilesTab } from '@/components/views/details/FilesTab';
import { RefreshHistoryTab } from '@/components/views/details/RefreshHistoryTab';
import { SQLTab } from '@/components/views/details/SQLTab';
import { DependencyGraph } from '@/components/views/DependencyGraph';

/**
 * ViewDetailsPage Component
 *
 * Main view details page with tabbed interface.
 *
 * @example
 * ```tsx
 * <Route path="/views/:id" element={<ViewDetailsPage />} />
 * ```
 */
export const ViewDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isMaterializing, setIsMaterializing] = useState(false);

  // Fetch view data - poll every 3s while materialization is in progress
  const { data: view, isLoading, error } = useView(id || '', {
    enabled: !!id,
    refetchInterval: isMaterializing ? 3000 : false,
  });

  // Stop polling once view_type changes to 'materialized'. Adjusted during
  // render (not an effect) via previous-value mirrors of `isMaterializing`
  // and `view?.view_type` — both stable primitives (boolean / string) — so
  // the check fires whenever either changes alone, matching the original
  // effect's [isMaterializing, view?.view_type] deps. Converges on the very
  // next render.
  const [prevIsMaterializing, setPrevIsMaterializing] = useState(isMaterializing);
  const [prevViewType, setPrevViewType] = useState(view?.view_type);
  if (isMaterializing !== prevIsMaterializing || view?.view_type !== prevViewType) {
    setPrevIsMaterializing(isMaterializing);
    setPrevViewType(view?.view_type);
    if (isMaterializing && view?.view_type === 'materialized') {
      setIsMaterializing(false);
    }
  }

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error || !view) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message || 'View not found'}
            <Button variant="link" onClick={() => navigate('/views')} className="ml-2">
              Back to Views
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/views')} className="mb-2">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Views
      </Button>

      {/* View Header with Actions */}
      <ViewHeader view={view} onMaterializeStarted={() => setIsMaterializing(true)} />

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="data">Data Preview</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
          <TabsTrigger value="columns">Columns</TabsTrigger>
          <TabsTrigger value="dependencies">Dependencies</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="history">Refresh History</TabsTrigger>
          <TabsTrigger value="sql">SQL</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <OverviewTab view={view} />
        </TabsContent>

        <TabsContent value="data" className="space-y-4 mt-6">
          <DataPreviewTab viewId={view.id} viewColumns={view.columns} />
        </TabsContent>

        <TabsContent value="rules" className="space-y-4 mt-6">
          <RulesTab view={view} />
        </TabsContent>

        <TabsContent value="columns" className="space-y-4 mt-6">
          <ColumnsTab view={view} />
        </TabsContent>

        <TabsContent value="dependencies" className="space-y-4 mt-6">
          <DependencyGraph viewId={view.id} viewName={view.name} direction="both" />
        </TabsContent>

        <TabsContent value="files" className="space-y-4 mt-6">
          <FilesTab viewId={view.id} />
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-6">
          <RefreshHistoryTab viewId={view.id} />
        </TabsContent>

        <TabsContent value="sql" className="space-y-4 mt-6">
          <SQLTab view={view} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
